/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { useCallback, useContext, useMemo, useState } from 'react';

import { Colors } from '../../../theme';
import { Link } from '../../navigation/Link';

import PageNavigationContext from './PageNavigationContext';
import type { PageNavigationRowProps } from './types';

const Styles = {
  link: css`
    display: block !important;
    text-decoration: none !important;

    & {
      &:focus {
        border-radius: 3px !important;
        color: ${Colors.UiPrimaryStrongest} !important;
        outline: 2px solid ${Colors.UiPrimaryStrongest} !important;
        outline-offset: -1px !important;
      }
    }
  `,
  row: css`
    width: 100%;

    & {
      &:focus {
        border-radius: 3px !important;
        color: ${Colors.UiPrimaryStrongest} !important;
        outline: 2px solid ${Colors.UiPrimaryStrongest} !important;
        outline-offset: -1px !important;
      }
    }
  `,
};

/**
 * PageNavigationRow is the list item wrapper around list item content
 * and possibly nested lists, if provided. This component handles tracking
 * hover state and onSelect behaviour, which are common to all rows, and
 * something we don't want to duplicate unnecessarily.
 * Note that we pass the list item content in the `content` prop, and an
 * optional nested list in the `nestedList` prop. An approach like this
 * is needed because the contents in `content` may require knowledge about
 * the hover state, which we couldn't pass down if we'd sent the list
 * item contents via the `children` prop.
 */
export default function PageNavigationRow({
  content: Content,
  isSelectable = true,
  item,
  nestedList: Children,
  onSelect,
}: PageNavigationRowProps) {
  const [hover, setHover] = useState(false);
  const context = useContext(PageNavigationContext);
  const { id, setId } = context;
  const selected = item.id === id;

  const handleOnSelect = useCallback(
    (event: React.SyntheticEvent) => {
      if (onSelect) {
        // Remove the children property before passing `item` to the callback
        const { children, ...itemWithNoChildren } = item;
        onSelect(itemWithNoChildren, event);
      }

      if (isSelectable) {
        // Set the selected state to this row using item.id.
        setId(item.id);
      }
    },
    [isSelectable, item, onSelect, setId]
  );

  // We don't require `item` instances to have an `href` property, but
  // this means that items without an `href` are not allowed to use `Link`.
  const linkContent = useMemo(
    () =>
      item.href ? (
        <Link
          css={Styles.link}
          href={item.href}
          onBlur={() => setHover(false)}
          onClick={(e: React.MouseEvent) => {
            handleOnSelect(e);
            e.stopPropagation();
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              handleOnSelect(e);
              e.stopPropagation();
            } else if (e.key === ' ') {
              e.preventDefault();
            }
          }}
          onFocus={() => setHover(true)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <Content hover={hover} selected={selected} />
        </Link>
      ) : (
        <div
          aria-label={item.title}
          css={Styles.row}
          data-zds
          onBlur={() => setHover(false)}
          onClick={(e) => handleOnSelect(e)}
          onFocus={() => setHover(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOnSelect(e);
            }
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          role="button"
          tabIndex={0}
        >
          <Content hover={hover} selected={selected} />
        </div>
      ),
    [handleOnSelect, hover, item.href, item.title, selected]
  );

  return (
    <li data-zds>
      {linkContent}
      {Children && <Children />}
    </li>
  );
}
